import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatMoney, formatPercent } from '@/lib/money';

import {
  SUBSCRIPTION_REASON_LIMITS,
  addTenantSubscriptionItem,
  endTenantSubscriptionItem,
  getTenantSubscription,
  tenantSubscriptionQueryKey,
  updateTenantSubscriptionItem,
  type BillableModule,
  type SubscriptionItem,
  type TenantSubscription,
} from '../api/subscription.graphql';
import { formatLifecycleDate, statusLabel } from '../tenant-labels';
import styles from './TenantModulesTab.module.scss';

interface Props {
  tenantId: string;
  /** `platform.billing.write`: agregar, descontar y quitar líneas. */
  canWrite: boolean;
}

type Action =
  | { kind: 'add'; module: BillableModule }
  | { kind: 'discount'; item: SubscriptionItem }
  | { kind: 'listPrice'; item: SubscriptionItem }
  | { kind: 'end'; item: SubscriptionItem };

/** Cómo aporta el tenant al MRR según su estado (D4). */
function mrrSummary(subscription: TenantSubscription): string {
  const status = statusLabel(subscription.planStatus).toLowerCase();
  if (subscription.mrrKind === 'real') {
    return `MRR: ${formatMoney(subscription.mrr, subscription.currency)} al mes.`;
  }
  if (subscription.mrrKind === 'potential') {
    return (
      `MRR potencial: ${formatMoney(subscription.potentialMrr, subscription.currency)} al mes. ` +
      `En ${status} no suma al MRR real; pasa entero al convertir a pago.`
    );
  }
  return (
    `Contratado: ${formatMoney(subscription.contractedMrr, subscription.currency)} al mes. ` +
    `Con el tenant ${status} no suma al MRR.`
  );
}

function parseDiscount(text: string): number | null {
  const value = Number(text.replace(',', '.'));
  return text.trim() !== '' && Number.isFinite(value) ? value : null;
}

/**
 * OS-1723 — líneas de suscripción del tenant (pestaña Facturación). Un módulo habilitado no se
 * cobra hasta agregarlo aquí (o al habilitarlo, confirmándolo). La línea guarda el precio
 * contratado: un cambio en la lista de precios no la modifica salvo que se aplique a mano.
 */
export function TenantSubscriptionSection({ tenantId, canWrite }: Props): JSX.Element {
  const qc = useQueryClient();
  const idPrefix = useId();
  const queryKey = tenantSubscriptionQueryKey(tenantId);
  const query = useQuery({ queryKey, queryFn: () => getTenantSubscription(tenantId) });
  const [action, setAction] = useState<Action | null>(null);
  const [discountText, setDiscountText] = useState('');
  const [notice, setNotice] = useState('');

  const mutation = useMutation({
    mutationFn: ({ current, reason }: { current: Action; reason: string }) => {
      switch (current.kind) {
        case 'add':
          return addTenantSubscriptionItem(tenantId, current.module.moduleKey);
        case 'discount':
          return updateTenantSubscriptionItem(tenantId, current.item.id, {
            discountPct: parseDiscount(discountText) ?? 0,
            reason,
          });
        case 'listPrice':
          return updateTenantSubscriptionItem(tenantId, current.item.id, {
            applyListPrice: true,
            reason,
          });
        case 'end':
          return endTenantSubscriptionItem(tenantId, current.item.id, reason);
      }
    },
    onSuccess: (next, { current }) => {
      qc.setQueryData(queryKey, next);
      // El MRR del listado, de la ficha y del dashboard se recalculan.
      void qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      void qc.invalidateQueries({ queryKey: ['platform-summary'] });
      const name = current.kind === 'add' ? current.module.name : current.item.moduleName;
      setNotice(
        {
          add: `${name} agregado a la suscripción.`,
          discount: `Descuento de ${name} actualizado.`,
          listPrice: `${name} quedó con el precio de lista vigente.`,
          end: `${name} salió de la suscripción.`,
        }[current.kind] + ' El cambio quedó auditado.',
      );
      setAction(null);
    },
  });

  function open(next: Action): void {
    mutation.reset();
    setNotice('');
    setDiscountText(next.kind === 'discount' ? String(next.item.discountPct) : '');
    setAction(next);
  }

  if (query.isLoading) {return <p className={styles.muted} role="status">Cargando la suscripción…</p>;}
  if (query.isError || !query.data) {
    return (
      <p className={styles.errorBox} role="alert">
        No se pudo cargar la suscripción: {query.error?.message ?? 'error desconocido'}
      </p>
    );
  }

  const subscription = query.data;
  const { currency } = subscription;
  const cancelled = subscription.planStatus === 'cancelled';
  const discount = parseDiscount(discountText);
  const discountValid =
    discount !== null &&
    discount >= 0 &&
    discount <= subscription.discountLimitPct &&
    /^\d+([.,]\d{1,2})?$/.test(discountText.trim());

  return (
    <section className={styles.panel} aria-labelledby={`${idPrefix}-title`}>
      <h2 id={`${idPrefix}-title`} className={styles.panelTitle}>
        Suscripción
      </h2>
      <p>{mrrSummary(subscription)}</p>
      <p className={styles.muted}>
        Montos mensuales en {currency}. Solo los módulos agregados aquí se cobran; habilitar un
        módulo no crea un cobro. OpsSynk todavía no genera facturas ni cobros automáticos.
      </p>
      <p className={styles.notice} role="status" aria-live="polite">
        {notice}
      </p>

      {subscription.items.length === 0 ? (
        <p className={styles.muted}>Ningún módulo en la suscripción.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label="Líneas de suscripción vigentes">
            <thead>
              <tr>
                <th scope="col">Módulo</th>
                <th scope="col">Precio contratado</th>
                <th scope="col">Descuento</th>
                <th scope="col">Valor mensual</th>
                <th scope="col">Desde</th>
                {canWrite ? <th scope="col">Acciones</th> : null}
              </tr>
            </thead>
            <tbody>
              {subscription.items.map((item) => {
                const listDiffers = item.listPrice !== null && item.listPrice !== item.unitPrice;
                return (
                  <tr key={item.id}>
                    <th scope="row">{item.moduleName}</th>
                    <td>
                      {formatMoney(item.unitPrice, item.currency)}
                      {listDiffers ? (
                        <span className={styles.description}>
                          {' '}
                          Lista vigente: {formatMoney(item.listPrice, item.currency)}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {item.discountPct > 0 ? formatPercent(item.discountPct) : '—'}
                      {item.discountReason && item.discountPct > 0 ? (
                        <span className={styles.description}> {item.discountReason}</span>
                      ) : null}
                    </td>
                    <td>{formatMoney(item.monthlyAmount, item.currency)}</td>
                    <td>{formatLifecycleDate(item.startsAt)}</td>
                    {canWrite ? (
                      <td>
                        <div className={styles.stack}>
                          <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => open({ kind: 'discount', item })}
                            aria-label={`Cambiar descuento de ${item.moduleName}`}
                          >
                            Descuento
                          </button>
                          {listDiffers ? (
                            <button
                              type="button"
                              className={styles.linkBtn}
                              onClick={() => open({ kind: 'listPrice', item })}
                              aria-label={`Aplicar precio de lista a ${item.moduleName}`}
                            >
                              Aplicar precio de lista
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={() => open({ kind: 'end', item })}
                            aria-label={`Quitar ${item.moduleName} de la suscripción`}
                          >
                            Quitar
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {subscription.billableModules.length > 0 ? (
        <>
          <h3 className={styles.sectionTitle}>Módulos habilitados sin cobro</h3>
          <ul className={styles.stack}>
            {subscription.billableModules.map((module) => (
              <li key={module.moduleKey}>
                {module.name}
                {module.isCore ? <span className={styles.badge}> base</span> : null}{' '}
                <span className={styles.muted}>
                  {module.listPrice === null
                    ? '— sin precio de lista (lo define un Super en Precios)'
                    : `— lista ${formatMoney(module.listPrice, currency)} al mes`}
                </span>{' '}
                {canWrite && module.listPrice !== null && !cancelled ? (
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => open({ kind: 'add', module })}
                    aria-label={`Agregar ${module.name} a la suscripción`}
                  >
                    Agregar
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {subscription.endedItems.length > 0 ? (
        <details>
          <summary>Líneas cerradas ({subscription.endedItems.length})</summary>
          <ul className={styles.stack}>
            {subscription.endedItems.map((item) => (
              <li key={item.id} className={styles.muted}>
                {item.moduleName}: {formatMoney(item.monthlyAmount, item.currency)} al mes, del{' '}
                {formatLifecycleDate(item.startsAt)} al {item.endsAt ? formatLifecycleDate(item.endsAt) : '—'} (
                {item.endReason === 'module_disabled' ? 'módulo deshabilitado' : 'quitado a mano'})
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {action?.kind === 'add' ? (
        <ConfirmDialog
          title={`Agregar ${action.module.name} a la suscripción`}
          confirmLabel="Agregar"
          pending={mutation.isPending}
          error={mutation.error?.message ?? null}
          onConfirm={() => mutation.mutate({ current: action, reason: '' })}
          onCancel={() => setAction(null)}
        >
          <p>
            Se cobrará {formatMoney(action.module.listPrice, currency)} al mes (precio de lista
            vigente). Ese precio queda guardado en la línea: si la lista cambia después, esta línea
            no cambia.
          </p>
          {subscription.mrrKind === 'potential' ? (
            <p>Mientras el tenant siga en piloto suma al MRR potencial, no al real.</p>
          ) : null}
        </ConfirmDialog>
      ) : null}

      {action?.kind === 'discount' ? (
        <ConfirmDialog
          title={`Descuento de ${action.item.moduleName}`}
          confirmLabel="Guardar descuento"
          pending={mutation.isPending}
          confirmDisabled={!discountValid}
          error={mutation.error?.message ?? null}
          reason={{
            label: 'Motivo del descuento (queda en la auditoría)',
            placeholder: 'Ej.: negociación de renovación anual',
            ...SUBSCRIPTION_REASON_LIMITS,
          }}
          onConfirm={(reason) => mutation.mutate({ current: action, reason })}
          onCancel={() => setAction(null)}
        >
          <label className={styles.stack}>
            Descuento (%)
            <input
              type="text"
              inputMode="decimal"
              value={discountText}
              onChange={(event) => setDiscountText(event.target.value)}
              aria-describedby={`${idPrefix}-discount-hint`}
              aria-invalid={!discountValid}
            />
          </label>
          <p id={`${idPrefix}-discount-hint`} className={styles.muted}>
            Tu rol puede aplicar hasta {formatPercent(subscription.discountLimitPct)}. Precio
            contratado {formatMoney(action.item.unitPrice, action.item.currency)}
            {discountValid && discount !== null
              ? `; quedaría en ${formatMoney(
                  Math.round(action.item.unitPrice * (100 - discount)) / 100,
                  action.item.currency,
                )} al mes.`
              : '.'}
          </p>
        </ConfirmDialog>
      ) : null}

      {action?.kind === 'listPrice' ? (
        <ConfirmDialog
          title={`Aplicar precio de lista a ${action.item.moduleName}`}
          confirmLabel="Aplicar precio"
          pending={mutation.isPending}
          error={mutation.error?.message ?? null}
          reason={{
            label: 'Motivo (queda en la auditoría)',
            placeholder: 'Ej.: renovación con la lista 2027',
            ...SUBSCRIPTION_REASON_LIMITS,
          }}
          onConfirm={(reason) => mutation.mutate({ current: action, reason })}
          onCancel={() => setAction(null)}
        >
          <p>
            El precio contratado pasa de {formatMoney(action.item.unitPrice, action.item.currency)}{' '}
            a {formatMoney(action.item.listPrice, action.item.currency)} al mes. El descuento se
            conserva.
          </p>
        </ConfirmDialog>
      ) : null}

      {action?.kind === 'end' ? (
        <ConfirmDialog
          title={`Quitar ${action.item.moduleName} de la suscripción`}
          confirmLabel="Quitar"
          danger
          pending={mutation.isPending}
          error={mutation.error?.message ?? null}
          reason={{
            label: 'Motivo (queda en la auditoría)',
            placeholder: 'Ej.: el módulo queda de cortesía hasta la renovación',
            ...SUBSCRIPTION_REASON_LIMITS,
          }}
          onConfirm={(reason) => mutation.mutate({ current: action, reason })}
          onCancel={() => setAction(null)}
        >
          <p>
            El MRR baja {formatMoney(action.item.monthlyAmount, action.item.currency)} al mes. El
            módulo sigue habilitado; solo deja de cobrarse.
          </p>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}
